import { db, functions } from "@/firebase";
import {
  doc,
  collection,
  onSnapshot,
  query,
  orderBy,
  runTransaction,
  addDoc,
  updateDoc,
  setDoc,
  getDocs,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { colorForOwner } from "@/components/alice/normalize";
import { beginReply, endReply } from "@/utils/replyAttention";

// How often the active typist refreshes the idle compose-lock heartbeat.
const TYPING_HEARTBEAT_MS = 3000;
// A lock whose heartbeat is older than this is treated as free, so an
// abandoned tab/crash never blocks the project forever.
const TYPING_STALE_MS = 10000;
// Local clock tick so staleness re-evaluates even when no snapshot arrives.
const CLOCK_TICK_MS = 2000;

// Communal (shared, project-based) chat behaviour for ProjectChat.vue.
// Personal-mode logic in the component is untouched; this mixin only adds the
// communal data source, realtime subscriptions, the idle typing-lock, and the
// queue-aware send path. The component template renders both modes via the
// shared `visibleMessages` + msg* attribution helpers below.
export default {
  data() {
    return {
      // "personal" (default, existing behaviour) or "communal" (shared thread).
      chatMode: "personal",
      communalMessages: [],
      communalMain: null,
      communalUnsub: null,
      communalMainUnsub: null,
      typingLockHeld: false,
      typingHeartbeatTimer: null,
      clockTimer: null,
      clearingCommunal: false,
      compressingCommunal: false,
      // Which archived session is being viewed; null = follow the active one.
      communalViewingVersion: null,
      now: 0,
      // Id of the newest message in the previous snapshot, so status-only
      // updates (queued→answering→answered) don't move the scroll position.
      communalLastMsgId: null,
      communalInitialSnap: true,
      // My queued/answering questions registered with the tab indicator.
      communalReplyIds: new Set(),
    };
  },
  computed: {
    // True while a question is being answered (or queued). In this state the
    // idle typing-lock is lifted and everyone may compose + queue in parallel.
    isAnswering() {
      if (this.communalMain && this.communalMain.processing) return true;
      return (this.communalMessages || []).some(
        (m) => m.status === "queued" || m.status === "answering"
      );
    },
    // The current idle compose-lock holder, or null if free/stale.
    typingLockHolder() {
      const lock = this.communalMain && this.communalMain.typingLock;
      if (!lock || !lock.heartbeat) return null;
      if (this.now - lock.heartbeat >= TYPING_STALE_MS) return null;
      return lock;
    },
    // Someone else is composing from idle → my input is disabled.
    lockedByOther() {
      if (this.isAnswering) return false;
      const holder = this.typingLockHolder;
      return !!(
        holder &&
        holder.userUid &&
        holder.userUid !== (this.user && this.user.uid)
      );
    },
    communalQueueCount() {
      return (this.communalMessages || []).filter((m) => m.status === "queued")
        .length;
    },
    // Session/versioning state, mirroring the personal chat's
    // activeVersion/sessions but sourced live from the main-doc snapshot.
    communalActiveVersion() {
      return this.communalMain?.activeVersion ?? 1;
    },
    communalSessions() {
      return this.communalMain?.sessions ?? [];
    },
    communalResolvedViewingVersion() {
      return this.communalViewingVersion ?? this.communalActiveVersion;
    },
    communalIsViewingActive() {
      return this.communalResolvedViewingVersion === this.communalActiveVersion;
    },
    communalRenderable() {
      // All sessions arrive in one snapshot; the viewed session is a pure
      // client-side filter (missing version = legacy, session 1).
      const viewing = this.communalResolvedViewingVersion;
      return (this.communalMessages || []).filter((m) => {
        if ((m.version ?? 1) !== viewing) return false;
        if (m.role !== "user" && m.role !== "assistant") return false;
        const text =
          typeof m.content === "string"
            ? m.content
            : typeof m.text === "string"
            ? m.text
            : "";
        return !!text.trim();
      });
    },
    // The list the template iterates, regardless of mode.
    visibleMessages() {
      return this.chatMode === "communal"
        ? this.communalRenderable
        : this.renderableMessages;
    },
    sendButtonLabel() {
      return this.chatMode === "communal" && this.isAnswering
        ? "Add to queue"
        : "Send";
    },
    inputDisabled() {
      if (this.chatMode === "communal") return this.lockedByOther;
      return this.chatLoading || !!this.chatStartError;
    },
    inputPlaceholder() {
      if (this.chatMode === "communal" && this.lockedByOther) {
        const holder = this.typingLockHolder;
        const name =
          holder && holder.userName
            ? holder.userName.split(/[\s@]/)[0]
            : "Someone";
        return `${name} is typing…`;
      }
      if (this.chatMode === "communal" && this.isAnswering) {
        return "Add a question to the queue…";
      }
      return "Ask anything about your data…";
    },
    // Model label for the shared "Alice is thinking…" indicator.
    pendingModelLabel() {
      if (this.chatMode === "communal") {
        const answering = (this.communalMessages || []).find(
          (m) => m.status === "answering"
        );
        return answering ? this.msgModelLabel(answering) : "Alice";
      }
      return this.modelLabel;
    },
  },
  watch: {
    chatMode(mode, prev) {
      if (mode === "communal") {
        this.communalViewingVersion = null; // always land on the active session
        this.subscribeCommunal();
      } else if (prev === "communal") {
        this.unsubscribeCommunal();
      }
    },
    input(val) {
      if (this.chatMode !== "communal") return;
      if (!this.communalIsViewingActive) return; // archived view is read-only
      if (this.isAnswering) return; // parallel queueing — no idle lock
      if (String(val || "").trim()) this.ensureTypingLock();
      else this.releaseTypingLockIfMine();
    },
    isAnswering(answering) {
      // Once answering begins, the idle compose-lock is meaningless — drop it.
      if (answering) this.releaseTypingLockIfMine();
    },
  },
  beforeUnmount() {
    this.unsubscribeCommunal();
  },
  methods: {
    communalMainRef() {
      const projectUid =
        (this.project && this.project.uid) || this.$route.params.id;
      return doc(db, "projects", projectUid, "communalChat", "main");
    },
    communalMessagesRef() {
      const projectUid =
        (this.project && this.project.uid) || this.$route.params.id;
      return collection(
        db,
        "projects",
        projectUid,
        "communalChat",
        "main",
        "messages"
      );
    },

    subscribeCommunal() {
      this.unsubscribeCommunal();
      this.now = Date.now();
      this.clockTimer = setInterval(() => {
        this.now = Date.now();
      }, CLOCK_TICK_MS);

      this.communalMainUnsub = onSnapshot(this.communalMainRef(), (snap) => {
        this.communalMain = snap.exists() ? snap.data() : null;
      });
      this.communalLastMsgId = null;
      this.communalInitialSnap = true;
      this.communalUnsub = onSnapshot(
        query(this.communalMessagesRef(), orderBy("timestamp")),
        (snap) => {
          this.communalMessages = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
          this.trackCommunalReplies();
          const msgs = this.communalMessages;
          const last = msgs.length ? msgs[msgs.length - 1] : null;
          const lastId = last ? last.id : null;
          const isNewMessage = lastId !== this.communalLastMsgId;
          const initial = this.communalInitialSnap;
          this.communalInitialSnap = false;
          this.communalLastMsgId = lastId;
          if (!isNewMessage) return;
          this.$nextTick(() => {
            if (!initial && last && last.role === "assistant") {
              this.scrollToAnswerStart();
            } else {
              this.scrollToBottom();
            }
          });
        }
      );
    },

    unsubscribeCommunal() {
      if (this.communalUnsub) {
        this.communalUnsub();
        this.communalUnsub = null;
      }
      if (this.communalMainUnsub) {
        this.communalMainUnsub();
        this.communalMainUnsub = null;
      }
      if (this.clockTimer) {
        clearInterval(this.clockTimer);
        this.clockTimer = null;
      }
      this.stopTypingHeartbeat();
      this.releaseTypingLockIfMine();
      // Unsubscribed → we can't see these settle; end them silently.
      this.communalReplyIds.forEach((id) => endReply(`communal:${id}`, null));
      this.communalReplyIds.clear();
    },

    // Feed the tab indicator: only my own questions count, so a teammate's
    // queue doesn't keep my tab busy or ping me. A question leaving
    // queued/answering settles as answered, error, or deleted (silent).
    trackCommunalReplies() {
      const me = this.user && this.user.uid;
      const open = new Set(
        (this.communalMessages || [])
          .filter(
            (m) =>
              m.role === "user" &&
              me &&
              m.authorUid === me &&
              (m.status === "queued" || m.status === "answering")
          )
          .map((m) => m.id)
      );
      open.forEach((id) => {
        if (this.communalReplyIds.has(id)) return;
        this.communalReplyIds.add(id);
        beginReply(`communal:${id}`);
      });
      [...this.communalReplyIds].forEach((id) => {
        if (open.has(id)) return;
        this.communalReplyIds.delete(id);
        const m = this.communalMessages.find((x) => x.id === id);
        const status = m && m.status;
        endReply(
          `communal:${id}`,
          status === "answered" || status === "error"
            ? this.replyNotice(status === "answered")
            : null
        );
      });
    },

    // ----- Idle compose-lock -----
    async ensureTypingLock() {
      if (this.typingLockHeld) return;
      const ok = await this.acquireTypingLock();
      if (ok) {
        this.typingLockHeld = true;
        this.startTypingHeartbeat();
      }
    },
    async acquireTypingLock() {
      const main = this.communalMainRef();
      const me = this.user && this.user.uid;
      try {
        return await runTransaction(db, async (t) => {
          const snap = await t.get(main);
          const data = snap.exists() ? snap.data() : {};
          const lock = data.typingLock;
          const now = Date.now();
          const fresh =
            lock && lock.heartbeat && now - lock.heartbeat < TYPING_STALE_MS;
          if (fresh && lock.userUid !== me) return false;
          t.set(
            main,
            {
              typingLock: {
                userUid: me || null,
                userName: this.userName || null,
                heartbeat: now,
              },
              updatedAt: now,
            },
            { merge: true }
          );
          return true;
        });
      } catch (err) {
        console.error("acquireTypingLock failed:", err);
        return false;
      }
    },
    startTypingHeartbeat() {
      this.stopTypingHeartbeat();
      this.typingHeartbeatTimer = setInterval(() => {
        if (!this.typingLockHeld) return;
        setDoc(
          this.communalMainRef(),
          {
            typingLock: {
              userUid: (this.user && this.user.uid) || null,
              userName: this.userName || null,
              heartbeat: Date.now(),
            },
          },
          { merge: true }
        ).catch((err) => console.error("typing heartbeat failed:", err));
      }, TYPING_HEARTBEAT_MS);
    },
    stopTypingHeartbeat() {
      if (this.typingHeartbeatTimer) {
        clearInterval(this.typingHeartbeatTimer);
        this.typingHeartbeatTimer = null;
      }
    },
    async releaseTypingLockIfMine() {
      this.stopTypingHeartbeat();
      if (!this.typingLockHeld) return;
      this.typingLockHeld = false;
      const main = this.communalMainRef();
      const me = this.user && this.user.uid;
      try {
        await runTransaction(db, async (t) => {
          const snap = await t.get(main);
          const lock = snap.exists() && snap.data().typingLock;
          if (lock && lock.userUid === me) {
            t.set(
              main,
              { typingLock: null, updatedAt: Date.now() },
              { merge: true }
            );
          }
        });
      } catch (err) {
        console.error("releaseTypingLock failed:", err);
      }
    },

    // ----- Send into the shared queue -----
    async sendCommunal() {
      if (!this.communalIsViewingActive) return;
      if (this.lockedByOther || this.uploadingAttachments) return;
      const text = String(this.input || "").trim();
      const hasAttachments = this.pendingAttachments.length > 0;
      if (!text && !hasAttachments) return;
      this.notice = "";

      let attachments = [];
      if (hasAttachments) {
        this.uploadingAttachments = true;
        try {
          attachments = await this.uploadPendingAttachments();
        } catch (error) {
          console.error("attachment upload failed:", error);
          alert("Could not upload attachments. Please try again.");
          this.uploadingAttachments = false;
          return;
        }
        this.uploadingAttachments = false;
      }

      let content;
      if (attachments.length) {
        content = [];
        if (text) content.push({ type: "input_text", text });
        for (const a of attachments) {
          if (a.contentType.startsWith("image/")) {
            content.push({ type: "input_image", image_url: a.url });
          } else {
            content.push({ type: "input_file", file_url: a.url });
          }
        }
      } else {
        content = text;
      }

      const msg = {
        role: "user",
        content,
        timestamp: Date.now(),
        queuedAt: serverTimestamp(),
        status: "queued",
        // Stamped with the session as this client sees it; the backend claim
        // re-stamps if a compress bumped the version in the meantime.
        version: this.communalActiveVersion,
        authorUid: (this.user && this.user.uid) || null,
        authorName: this.userName || null,
        authorPhoto: this.userPhoto || null,
        model: this.model,
      };
      if (attachments.length) {
        msg.text = text;
        msg.attachments = attachments;
      }

      // Free the UI immediately; onSnapshot reflects the message (and everyone
      // else's) — no optimistic push needed.
      this.input = "";
      this.pendingAttachments = [];
      this.$nextTick(() => {
        if (this.$refs.textareaRef)
          this.$refs.textareaRef.style.height = "auto";
      });
      await this.releaseTypingLockIfMine();

      try {
        const ref = await addDoc(this.communalMessagesRef(), msg);
        await updateDoc(ref, { uid: ref.id });
      } catch (error) {
        console.error("communal send failed:", error);
        this.notice = "Could not send your message. Please try again.";
      }
    },

    // ----- Clear the current team session (for the whole team) -----
    // Scoped to the active session's messages so archived (compressed)
    // sessions stay viewable — same semantics as personal clearChat. Blocked
    // while a question is queued/answering: the drain loop updates the claimed
    // message doc after generating, and deleting it out from under the loop
    // would fail that update and stall the mutex until stale-lock recovery.
    async clearCommunalChat() {
      if (this.isAnswering || this.clearingCommunal || this.compressingCommunal)
        return;
      const ok = confirm(
        "Clear the team chat? This deletes the current session's messages for everyone in this project."
      );
      if (!ok) return;
      this.clearingCommunal = true;
      this.notice = "";
      try {
        const snap = await getDocs(this.communalMessagesRef());
        const docs = snap.docs.filter(
          (d) => (d.data().version ?? 1) === this.communalActiveVersion
        );
        // Firestore batches cap at 500 writes.
        for (let i = 0; i < docs.length; i += 500) {
          const batch = writeBatch(db);
          docs.slice(i, i + 500).forEach((d) => batch.delete(d.ref));
          await batch.commit();
        }
        await setDoc(
          this.communalMainRef(),
          {
            typingLock: null,
            processing: false,
            currentMessageUid: null,
            // Active skills are thread-sticky; clearing the thread resets them.
            activeSkillIds: [],
            lastAutoSkill: null,
            updatedAt: Date.now(),
          },
          { merge: true }
        );
      } catch (err) {
        console.error("clearCommunalChat failed:", err);
        this.notice = "Could not clear the team chat. Please try again.";
      } finally {
        this.clearingCommunal = false;
      }
    },

    // ----- Compress the current team session -----
    // Summarize + archive the active session and start a fresh one seeded
    // with the summary. The heavy lifting (queue mutex, migration, seed) is
    // the compressCommunalChat callable; this client just triggers it and
    // follows the main-doc snapshot to the new session.
    async compressCommunalChat() {
      if (
        this.compressingCommunal ||
        this.clearingCommunal ||
        this.isAnswering ||
        !this.communalIsViewingActive
      )
        return;
      const ok = confirm(
        "Compress the team chat for everyone? The current session will be archived and a new session will start, seeded with an AI summary."
      );
      if (!ok) return;
      this.compressingCommunal = true;
      this.notice = "";
      try {
        const compressFn = httpsCallable(functions, "compressCommunalChat", {
          timeout: 540000,
        });
        await compressFn({
          project: (this.project && this.project.uid) || this.$route.params.id,
          model: this.model,
        });
        this.communalViewingVersion = null; // follow the new active session
        this.notice = "Team chat compressed. New session started with summary.";
      } catch (err) {
        console.error("compressCommunalChat failed:", err);
        this.notice =
          (err && err.message) ||
          "Could not compress the team chat. Please try again.";
      } finally {
        this.compressingCommunal = false;
      }
    },

    // ----- Per-message attribution (mode-aware; used by the shared template) -----
    msgIsMine(m) {
      if (this.chatMode === "communal") {
        return !!(m.authorUid && this.user && m.authorUid === this.user.uid);
      }
      return true;
    },
    msgAuthorName(m) {
      if (this.chatMode === "communal") return m.authorName || "Teammate";
      return this.userName;
    },
    msgAuthorFirst(m) {
      const name = this.msgAuthorName(m);
      if (!name) return "you";
      return name.split(/[\s@]/)[0];
    },
    msgAuthorPhoto(m) {
      if (this.chatMode === "communal") return m.authorPhoto || "";
      return this.userPhoto;
    },
    msgAuthorColor(m) {
      return colorForOwner(this.msgAuthorName(m));
    },
    msgModelLabel(m) {
      if (this.chatMode === "communal") {
        const found = (this.modelOptions || []).find(
          (o) => o.value === m.model
        );
        return found ? found.title : m.model || "Alice";
      }
      return this.modelLabel;
    },
  },
};
